/**
 * citationPipeline — single entry point that runs the citation-link work
 * for one freshly-inserted section. Called by both Word-write paths
 * (``useWordWriteQueue`` for live SSE; ``loadRunIntoLiveDoc`` for post-
 * run hydration / Drafts-panel reload) so the two paths can't drift.
 *
 * # PIPELINE STEPS
 *  1. Renumber — assumed already done by ``sanitizeDraftForWord`` →
 *     ``renumberCitationsPerSection`` BEFORE the text reaches Word. The
 *     pipeline does not re-renumber; rerunning the renumber on Word-side
 *     text would risk diverging from the persisted draft. (If you need
 *     to fix hand-edited drift, do it before insertion.)
 *  2. Wrap — wrap every ``[N]`` token in the section's prose in an
 *     inline ``Word.ContentControl`` tagged ``asthra-cite:<sec>:<key>``
 *     via ``wrapCitationMarkers``. Idempotent.
 *  3. Refresh — under the current selection-change-polling event model
 *     this is a no-op; kept in the chain for shape so future signals
 *     (per-CC event registrations, cache invalidation, …) have a hook.
 *
 * # ERROR HANDLING
 *  Best-effort. A failure here must never block the write queue or the
 *  hydration loop from advancing to the next section. ``wrapCitationMarkers``
 *  already swallows its own errors; this wrapper adds one warn line so
 *  it's traceable in the console.
 */

import {
  refreshCitationMarkerEvents,
} from "./citationMarkerEvents";
import { wrapCitationMarkers } from "./citationMarkerWriter";

/** Result of a pipeline run. ``wrapped`` is the number of newly-created
 *  citation content controls (0 when the section had no citations or all
 *  were already wrapped — both legitimate). */
export interface CitationPipelineResult {
  wrapped: number;
}

/** Run the full post-section citation pipeline for ``sectionNumber``.
 *  Resolves once wrap + refresh are settled. Errors are logged and
 *  swallowed; the resolved result reflects whatever progress was made. */
export async function runPostSectionCitationPipeline(
  sectionNumber: string,
): Promise<CitationPipelineResult> {
  if (!sectionNumber) return { wrapped: 0 };
  let wrapped = 0;
  try {
    wrapped = await wrapCitationMarkers(sectionNumber);
  } catch (err) {
    console.warn(
      `[citationPipeline] wrap failed for §${sectionNumber} (non-fatal):`,
      err,
    );
  }
  try {
    await refreshCitationMarkerEvents();
  } catch (err) {
    console.warn(
      `[citationPipeline] refresh failed for §${sectionNumber} (non-fatal):`,
      err,
    );
  }
  return { wrapped };
}
